<script setup lang="ts">
import NativeDialog from '@/core/dialogsystem/NativeDialog.vue';
import CreatedUpdatedInfo from '@/core/elements/CreatedUpdatedInfo.vue';
import IconButton from '@/core/elements/IconButton.vue';
import { EIcon } from 'common/enum/misc.enum';
import { computed, nextTick, ref, useId, useTemplateRef } from 'vue';
import { useTabbedDetailDialogCloseLifecycle } from './useTabbedDetailDialogCloseLifecycle';
import type {
  ITabbedDetailDialogMetadata,
  ITabbedDetailDialogTab,
  TTabbedDetailDialogCloseReason,
  TTabbedDetailDialogHeight,
  TTabbedDetailDialogSize,
} from './tabbedDetailDialog.types';
import { getTabbedDetailDialogNavigationIndex } from './tabbedDetailDialog.navigation';

const props = withDefaults(
  defineProps<{
    activeTab: string;
    beforeClose?: (reason: TTabbedDetailDialogCloseReason) => boolean | Promise<boolean>;
    closeDisabled?: boolean;
    height?: TTabbedDetailDialogHeight;
    metadata?: ITabbedDetailDialogMetadata | null;
    modalOverlayActive?: boolean;
    size?: TTabbedDetailDialogSize;
    subtitle?: string;
    tabs: ITabbedDetailDialogTab[];
    title: string;
  }>(),
  {
    beforeClose: undefined,
    closeDisabled: false,
    height: 'default',
    metadata: null,
    modalOverlayActive: false,
    size: 'compact',
    subtitle: undefined,
  },
);

const emit = defineEmits<{
  close: [reason: TTabbedDetailDialogCloseReason];
  'update:activeTab': [value: string];
}>();

const dialogRef = useTemplateRef<InstanceType<typeof NativeDialog>>('dialogRef');
const contentRef = useTemplateRef<HTMLElement>('contentRef');
const headingId = `tabbed-detail-dialog-${useId()}`;
const panelId = `${headingId}-panel`;
const isRendered = ref(false);
const overlaysRendered = ref(false);
let closeReason: TTabbedDetailDialogCloseReason = 'programmatic';
let closePromise: Promise<void> | null = null;
let resolveClose: (() => void) | null = null;

const dialogSizeClass = computed(() => (props.size === 'wide' ? 'w-[960px]' : 'w-[640px]'));
const dialogHeightClass = computed(() => (props.height === 'tall' ? 'h-[860px]' : 'h-[720px]'));
const RESIZE_TRANSITION_PROPERTIES = new Set(['height', 'max-height', 'max-width', 'width']);

const show = async (): Promise<void> => {
  if (isRendered.value) return;
  isRendered.value = true;
  await nextTick();
  dialogRef.value?.showModal();
  overlaysRendered.value = true;
  await nextTick();
};

const hide = (reason: TTabbedDetailDialogCloseReason = 'programmatic'): Promise<void> => {
  if (!isRendered.value) return Promise.resolve();
  if (closePromise) return closePromise;
  closeReason = reason;
  const nativeDialog = dialogRef.value;
  if (!nativeDialog) {
    isRendered.value = false;
    emit('close', closeReason);
    closeReason = 'programmatic';
    return Promise.resolve();
  }
  closePromise = new Promise<void>((resolve) => {
    resolveClose = resolve;
  });
  nativeDialog.close();
  return closePromise;
};

const { closePending, requestClose } = useTabbedDetailDialogCloseLifecycle({
  beforeClose: (reason) => props.beforeClose?.(reason) ?? true,
  hide,
  isCloseDisabled: () => props.closeDisabled,
  isRendered: () => isRendered.value,
});

const handleNativeClose = (): void => {
  overlaysRendered.value = false;
  isRendered.value = false;
  closePending.value = false;
  emit('close', closeReason);
  closeReason = 'programmatic';
  resolveClose?.();
  resolveClose = null;
  closePromise = null;
};

const scrollToTop = (): void => {
  contentRef.value?.scrollTo({ behavior: 'smooth', top: 0 });
};

const waitForResize = async (): Promise<void> => {
  await nextTick();
  const dialogElement = dialogRef.value?.element;
  if (!dialogElement) return;

  const resizeTransitions = dialogElement
    .getAnimations()
    .filter(
      (animation): animation is CSSTransition =>
        animation instanceof CSSTransition && RESIZE_TRANSITION_PROPERTIES.has(animation.transitionProperty),
    );
  await Promise.allSettled(resizeTransitions.map((transition) => transition.finished));
};

const handleTabKeydown = (event: KeyboardEvent, currentIndex: number): void => {
  const nextIndex = getTabbedDetailDialogNavigationIndex(event.key, currentIndex, props.tabs.length);
  if (nextIndex == null) return;

  event.preventDefault();
  emit('update:activeTab', props.tabs[nextIndex].value);
  const tabButtons = (event.currentTarget as HTMLElement).parentElement?.querySelectorAll<HTMLButtonElement>(
    '[role="tab"]',
  );
  tabButtons?.[nextIndex]?.focus();
};

defineExpose({ hide, requestClose, scrollToTop, show, waitForResize });
</script>

<template>
  <NativeDialog
    v-if="isRendered"
    ref="dialogRef"
    :aria-labelledby="headingId"
    :class="[
      dialogSizeClass,
      dialogHeightClass,
      'max-h-[calc(100dvh-32px)] max-w-[calc(100vw-32px)] overflow-hidden rounded-dialog border-0 bg-surface-primary p-0 shadow-popup outline-none',
      'transition-[width] duration-300 ease-in-out',
      'max-md:inset-0 max-md:m-0 max-md:h-dvh max-md:max-h-dvh max-md:w-screen max-md:max-w-none max-md:rounded-none max-md:shadow-none',
    ]"
    :can-close="() => !closeDisabled && !closePending"
    :close-on-backdrop-click="false"
    :close-on-cancel="false"
    @backdrop-click="requestClose('backdrop')"
    @cancel="requestClose('escape')"
    @close="handleNativeClose"
  >
    <div
      class="flex h-full min-h-0 flex-col [container-type:inline-size] max-md:pt-[env(safe-area-inset-top)]"
      :aria-hidden="modalOverlayActive || undefined"
      :inert="modalOverlayActive"
    >
      <!-- generic full-width banner above the header (e.g. a completed-status banner) -->
      <slot name="banner"></slot>

      <header
        class="flex h-header shrink-0 items-start justify-between gap-[14px] px-content py-lg max-md:h-[72px] max-md:min-h-[72px] max-md:items-center max-md:gap-md max-md:px-lg max-md:py-md"
      >
        <div class="min-w-0 flex-1 self-stretch">
          <div class="flex min-w-0 items-center gap-sm">
            <h1 :id="headingId" class="truncate text-body font-semibold leading-[1.7] text-primary">{{ title }}</h1>
            <slot name="title-badge"></slot>
          </div>
          <slot name="subtitle">
            <p
              v-if="subtitle"
              class="truncate text-body font-normal leading-[1.7] text-secondary max-md:text-footer max-md:leading-[1.6] max-md:tracking-[0.01em]"
            >
              {{ subtitle }}
            </p>
          </slot>
        </div>

        <div class="flex shrink-0 items-center gap-sm self-stretch">
          <div class="flex items-center gap-sm max-md:hidden">
            <slot name="header-status"></slot>
            <slot name="header-actions"></slot>
          </div>
          <div v-if="$slots['mobile-header-actions']" class="hidden items-center gap-sm max-md:flex">
            <slot name="mobile-header-actions"></slot>
          </div>
          <IconButton
            v-if="!modalOverlayActive"
            type="button"
            size="button"
            :icon-name="EIcon.X"
            :aria-label="$t('general.close')"
            theme="text"
            :disabled="closeDisabled || closePending"
            @click="requestClose('close-button')"
          />
        </div>
      </header>

      <nav
        class="flex h-12 shrink-0 items-end gap-lg border-b-[0.5px] border-divider px-content max-md:gap-0 max-md:px-0"
        role="tablist"
      >
        <button
          v-for="(tab, tabIndex) in tabs"
          :id="`${headingId}-tab-${tab.value}`"
          :key="tab.value"
          type="button"
          role="tab"
          class="relative flex h-12 min-h-button shrink-0 items-center text-body font-semibold leading-[1.7] outline-inner max-md:flex-1 max-md:justify-center"
          :class="activeTab === tab.value ? 'text-primary' : 'text-secondary'"
          :aria-selected="activeTab === tab.value"
          :aria-controls="panelId"
          :tabindex="activeTab === tab.value ? 0 : -1"
          @click="emit('update:activeTab', tab.value)"
          @keydown="handleTabKeydown($event, tabIndex)"
        >
          {{ tab.label }}
          <span v-if="activeTab === tab.value" class="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-tint" />
        </button>
      </nav>

      <div v-if="$slots['mobile-toolbar']" class="hidden shrink-0 max-md:block">
        <slot name="mobile-toolbar"></slot>
      </div>

      <main
        :id="panelId"
        ref="contentRef"
        class="min-h-0 flex-1 overflow-y-auto overscroll-contain"
        role="tabpanel"
        :aria-labelledby="`${headingId}-tab-${activeTab}`"
      >
        <slot :active-tab="activeTab"></slot>
      </main>

      <footer
        v-if="metadata || $slots['footer-leading'] || $slots.footer"
        class="mx-content shrink-0 border-t border-divider pb-content pt-lg max-md:mx-lg max-md:pb-mobile-safe-bottom-md"
      >
        <slot name="footer-leading"></slot>
        <CreatedUpdatedInfo
          v-if="metadata"
          :creator="metadata.creator"
          :created-at="metadata.createdAt"
          :updater="metadata.updater"
          :updated-at="metadata.updatedAt"
        />
        <slot name="footer"></slot>
      </footer>
    </div>

    <slot v-if="overlaysRendered" name="overlay"></slot>
  </NativeDialog>
</template>
